<template>
    <div id="PostDetailView">
        <NavBar />

        <main>
        <template v-if="post">
            <header class="post-hero">
                <b-container>
                    <nav class="post-breadcrumb" aria-label="Breadcrumb">
                        <router-link to="/blog">Blog</router-link>
                        <span>/</span>
                        <span>{{ post.title }}</span>
                    </nav>
                    <div class="hero-content">
                        <p class="post-kicker">CHILL BYTE / GUÍA EMPRESARIAL</p>
                        <h1 class="post-hero-title">{{ post.title }}</h1>
                        <div class="hero-meta-row">
                            <span class="post-date-badge">Publicado <time :datetime="post.date">{{ formattedDate(post.date) }}</time></span>
                            <span class="post-date-badge">Actualizado <time :datetime="post.updatedAt || post.date">{{ formattedDate(post.updatedAt || post.date) }}</time></span>
                            <span class="post-date-badge">{{ readingTime }} min de lectura</span>
                        </div>
                    </div>
                </b-container>
            </header>

            <b-container class="post-container">
                <div class="article-layout">
                    <article class="post-article">
                        <figure class="featured-image-wrapper">
                            <img
                                :src="require(`@/assets/img/blog/${post.image}.webp`)"
                                :alt="post.altImage"
                                class="featured-image"
                                fetchpriority="high"
                            />
                            <figcaption class="featured-caption">{{ post.altImage }}</figcaption>
                        </figure>

                        <div class="content-wrapper">
                            <section class="post-summary" aria-labelledby="resumen-post">
                                <h2 id="resumen-post" class="summary-title">Resumen ejecutivo</h2>
                                <p v-html="formatInline(post.summary)"></p>
                            </section>

                            <section v-if="post.keyTakeaways && post.keyTakeaways.length" class="takeaways" aria-labelledby="hallazgos-clave">
                                <h2 id="hallazgos-clave" class="section-title">Hallazgos clave</h2>
                                <ol class="ordered-list">
                                    <li v-for="(item, idx) in post.keyTakeaways" :key="`takeaway-${idx}`" v-html="formatInline(item)"></li>
                                </ol>
                            </section>

                            <section
                                v-for="section in postSections"
                                :id="section.id"
                                :key="section.id"
                                class="content-section"
                            >
                                <h2 class="section-title">{{ section.heading }}</h2>

                                <div
                                    v-for="(block, blockIndex) in section.blocks"
                                    :key="`${section.id}-${blockIndex}`"
                                    class="content-block"
                                >
                                    <h3 v-if="block.type === 'subheading'" class="content-subheading">{{ block.text }}</h3>

                                    <p v-if="block.type === 'paragraph'" class="content-paragraph" v-html="formatInline(block.text)"></p>

                                    <div
                                        v-else-if="block.type === 'callout'"
                                        class="callout"
                                        :class="`callout-${block.variant || 'info'}`"
                                    >
                                        <p class="callout-title" v-if="block.title" v-html="formatInline(block.title)"></p>
                                        <p class="callout-text" v-html="formatInline(block.text)"></p>
                                    </div>

                                    <ul v-else-if="block.type === 'unorderedList'" class="unordered-list">
                                        <li v-for="(item, itemIndex) in block.items" :key="itemIndex">
                                            <template v-if="isObjectItem(item)">
                                                <strong v-if="item.title" v-html="formatInline(item.title)"></strong>
                                                <span v-if="item.title && item.text">: </span>
                                                <span v-html="formatInline(item.text)"></span>
                                            </template>
                                            <template v-else>
                                                <span v-html="formatInline(item)"></span>
                                            </template>
                                        </li>
                                    </ul>

                                    <ol v-else-if="block.type === 'orderedList'" class="ordered-list">
                                        <li v-for="(item, itemIndex) in block.items" :key="itemIndex">
                                            <template v-if="isObjectItem(item)">
                                                <strong v-if="item.title" v-html="formatInline(item.title)"></strong>
                                                <span v-if="item.title && item.text">: </span>
                                                <span v-html="formatInline(item.text)"></span>
                                            </template>
                                            <template v-else>
                                                <span v-html="formatInline(item)"></span>
                                            </template>
                                        </li>
                                    </ol>

                                    <div
                                        v-else-if="block.type === 'table'"
                                        class="table-scroll"
                                        role="region"
                                        :aria-label="block.caption || 'Tabla de datos'"
                                        tabindex="0"
                                    >
                                        <table class="content-table">
                                            <caption v-if="block.caption">{{ block.caption }}</caption>
                                            <thead>
                                                <tr>
                                                    <th v-for="(header, headerIndex) in block.headers" :key="headerIndex" scope="col" v-html="formatInline(header)"></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="(row, rowIndex) in block.rows" :key="rowIndex">
                                                    <td v-for="(cell, cellIndex) in row" :key="cellIndex" v-html="formatInline(cell)"></td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    <figure v-else-if="block.type === 'image'" class="inline-image-wrapper">
                                        <img
                                            :src="require(`@/assets/img/blog/${block.image}.webp`)"
                                            :alt="block.alt || post.altImage"
                                            class="inline-image"
                                            loading="lazy"
                                        />
                                        <figcaption v-if="block.caption" class="inline-caption">{{ block.caption }}</figcaption>
                                    </figure>
                                </div>
                            </section>

                            <section class="share-section" aria-label="Compartir articulo">
                                <p class="share-title">Comparte este artículo</p>
                                <div class="share-actions">
                                    <button class="share-btn" @click="shareNative">Compartir</button>
                                    <button class="share-btn" @click="openShareUrl('whatsapp')">WhatsApp</button>
                                    <button class="share-btn" @click="openShareUrl('linkedin')">LinkedIn</button>
                                    <button class="share-btn" @click="openShareUrl('x')">X</button>
                                    <button class="share-btn" @click="copyPostUrl">Copiar enlace</button>
                                </div>
                                <p v-if="copyMessage" class="copy-message">{{ copyMessage }}</p>
                            </section>

                            <section class="author-section" aria-label="Autor del articulo">
                                <div class="author-card">
                                    <div class="author-info">
                                        <p class="author-label">Escrito por</p>
                                        <p class="author-name">{{ post.author }}</p>
                                        <p class="author-label">Revisado por</p>
                                        <p class="author-name">Equipo de consultoría de Chill Byte Solutions</p>
                                    </div>
                                </div>
                            </section>
                        </div>
                    </article>

                    <aside class="post-sidebar" aria-label="Indice del articulo">
                        <div class="toc-card">
                            <p class="toc-title">Índice del artículo</p>
                            <ol class="toc-list">
                                <li v-for="(entry) in tableOfContents" :key="entry.id">
                                    <a :href="`#${entry.id}`"> {{ entry.label }}</a>
                                </li>
                            </ol>
                        </div>
                    </aside>
                </div>
            </b-container>
        </template>

        <b-container v-else class="post-container">
            <div class="content-wrapper not-found-wrapper">
                <h1 class="section-title">Articulo no encontrado</h1>
                <p class="content-paragraph">
                    Esta publicacion no existe o la URL cambio. Regresa al blog para ver los articulos disponibles.
                </p>
                <router-link class="share-btn" :to="{ path: '/blog' }">Ir al blog</router-link>
            </div>
        </b-container>
        </main>

        <ContactForm />
        <FooterChillByte />
    </div>
</template>

<script>
import NavBar from "../components/Template/NavBar.vue";
import Footer from "../components/Template/Footer.vue";
import ContactForm from "@/components/Template/ContactForm.vue";
import { getBlogPostBySlug, SITE_URL } from "@/data/blogPosts";

export default {
    name: "PostDetailView",
    components: {
        NavBar,
        FooterChillByte: Footer,
        ContactForm,
    },
    data() {
        return {
            copyMessage: "",
        };
    },
    computed: {
        post() {
            return getBlogPostBySlug(this.$route.params.slug);
        },
        postUrl() {
            if (!this.post) {
                return `${SITE_URL}/blog`;
            }
            return `${SITE_URL}/blog/${this.post.slug}`;
        },
        postSections() {
            if (!this.post) {
                return [];
            }

            const richSections = Array.isArray(this.post.sections) ? this.post.sections : [];
            if (richSections.length) {
                return richSections.map((section, index) => ({
                    ...section,
                    id: section.id || this.slugify(section.heading || `seccion-${index + 1}`),
                    blocks: Array.isArray(section.blocks) ? section.blocks : [],
                }));
            }

            return [
                {
                    id: "seccion-1",
                    heading: this.post.subtitle1,
                    blocks: [{ type: "unorderedList", items: this.post.listSubtitles || [] }],
                },
                {
                    id: "seccion-2",
                    heading: this.post.subtitle2,
                    blocks: [{ type: "unorderedList", items: this.post.listSubtitles2 || [] }],
                },
            ];
        },
        tableOfContents() {
            return this.postSections.map((section) => ({
                id: section.id,
                label: section.heading,
            }));
        },
        readingTime() {
            const wordsPerMinute = 220;
            const words = this.articleWordCount;
            return Math.max(1, Math.ceil(words / wordsPerMinute));
        },
        articleWordCount() {
            if (!this.post) {
                return 0;
            }

            const pieces = [this.post.title, this.post.summary, this.post.excerpt];

            this.postSections.forEach((section) => {
                pieces.push(section.heading);
                (section.blocks || []).forEach((block) => {
                    pieces.push(block.text || "");
                    pieces.push(block.title || "");
                    if (Array.isArray(block.items)) {
                        block.items.forEach((item) => {
                            if (typeof item === "string") {
                                pieces.push(item);
                            } else if (item && typeof item === "object") {
                                pieces.push(item.title || "");
                                pieces.push(item.text || "");
                            }
                        });
                    }
                    if (block.type === "table") {
                        pieces.push(block.caption || "");
                        pieces.push(...(Array.isArray(block.headers) ? block.headers : []));
                        (Array.isArray(block.rows) ? block.rows : []).forEach((row) => {
                            if (Array.isArray(row)) {
                                pieces.push(...row);
                            }
                        });
                    }
                });
            });

            const joined = pieces.join(" ").replace(/\s+/g, " ").trim();
            if (!joined) {
                return 0;
            }

            return joined.split(" ").length;
        },
        blogPostingSchema() {
            if (!this.post) {
                return null;
            }

            return {
                "@context": "https://schema.org",
                "@type": "BlogPosting",
                headline: this.post.title,
                description: this.post.excerpt,
                image: [`${SITE_URL}/img/blog/${this.post.image}.webp`],
                datePublished: this.post.date,
                dateModified: this.post.updatedAt || this.post.date,
                url: this.postUrl,
                author: {
                    "@type": "Organization",
                    name: this.post.author,
                },
                publisher: {
                    "@type": "Organization",
                    name: "Chill Byte Solutions",
                    url: SITE_URL,
                    logo: {
                        "@type": "ImageObject",
                        url: `${SITE_URL}/img/homepage/logo.png`,
                    },
                },
                inLanguage: "es-MX",
                audience: {
                    "@type": "BusinessAudience",
                    audienceType: "Empresas que ya utilizan Oracle NetSuite",
                },
                about: "Optimizacion de procesos y operaciones en Oracle NetSuite",
                mainEntityOfPage: {
                    "@type": "WebPage",
                    "@id": this.postUrl,
                },
                keywords: (this.post.keywords || []).join(", "),
                articleSection: this.post.categories || [],
                wordCount: this.articleWordCount,
            };
        },
        breadcrumbSchema() {
            if (!this.post) {
                return null;
            }

            return {
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: [
                    {
                        "@type": "ListItem",
                        position: 1,
                        name: "Blog",
                        item: `${SITE_URL}/blog`,
                    },
                    {
                        "@type": "ListItem",
                        position: 2,
                        name: this.post.title,
                        item: this.postUrl,
                    },
                ],
            };
        },
    },
    metaInfo() {
        if (!this.post) {
            const title = "Articulo no encontrado | Chill Byte";
            const description = "La entrada solicitada no esta disponible. Revisa el blog para ver articulos de NetSuite, SAT y transformacion digital.";

            return {
                title,
                link: [{ rel: "canonical", href: `${SITE_URL}/blog` }],
                meta: [
                    { vmid: "description", name: "description", content: description },
                    { vmid: "robots", name: "robots", content: "noindex,follow" },
                ],
            };
        }

        const title = `${this.post.title} | Chill Byte`;
        const description = this.post.excerpt;
        const imageUrl = `${SITE_URL}/img/blog/${this.post.image}.webp`;
        const keywordSet = [...(this.post.keywords || []), "consultoria netsuite mexico", "optimizacion netsuite", "post implementacion netsuite"];

        return {
            title,
            link: [{ rel: "canonical", href: this.postUrl }],
            meta: [
                { vmid: "description", name: "description", content: description },
                { vmid: "robots", name: "robots", content: "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1" },
                { vmid: "keywords", name: "keywords", content: keywordSet.join(", ") },
                { vmid: "og:type", property: "og:type", content: "article" },
                { vmid: "og:locale", property: "og:locale", content: "es_MX" },
                { vmid: "og:title", property: "og:title", content: title },
                { vmid: "og:description", property: "og:description", content: description },
                { vmid: "og:url", property: "og:url", content: this.postUrl },
                { vmid: "og:image", property: "og:image", content: imageUrl },
                { vmid: "og:image:alt", property: "og:image:alt", content: this.post.altImage || this.post.title },
                { vmid: "article:published_time", property: "article:published_time", content: this.post.date },
                { vmid: "article:modified_time", property: "article:modified_time", content: this.post.updatedAt || this.post.date },
                { vmid: "article:author", property: "article:author", content: this.post.author },
                { vmid: "twitter:card", name: "twitter:card", content: "summary_large_image" },
                { vmid: "twitter:title", name: "twitter:title", content: title },
                { vmid: "twitter:description", name: "twitter:description", content: description },
                { vmid: "twitter:image", name: "twitter:image", content: imageUrl },
            ],
            script: [
                {
                    vmid: "blog-posting-schema",
                    type: "application/ld+json",
                    innerHTML: JSON.stringify(this.blogPostingSchema),
                },
                {
                    vmid: "blog-breadcrumb-schema",
                    type: "application/ld+json",
                    innerHTML: JSON.stringify(this.breadcrumbSchema),
                },
            ],
            __dangerouslyDisableSanitizersByTagID: {
                "blog-posting-schema": ["innerHTML"],
                "blog-breadcrumb-schema": ["innerHTML"],
            },
        };
    },
    methods: {
        formatInline(value) {
            const raw = String(value || "");
            if (!raw) {
                return "";
            }

            const escaped = raw
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/"/g, "&quot;")
                .replace(/'/g, "&#39;");

            const withBold = escaped.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
            const withUnderline = withBold.replace(/==(.+?)==/g, "<u>$1</u>");

            return withUnderline.replace(/\n/g, "<br>");
        },
        formattedDate(date) {
            try {
                return new Intl.DateTimeFormat("es-MX", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                }).format(new Date(date));
            } catch (error) {
                return date;
            }
        },
        slugify(value) {
            return (value || "")
                .toLowerCase()
                .trim()
                .replace(/[^a-z0-9\s-]/g, "")
                .replace(/\s+/g, "-")
                .replace(/-+/g, "-");
        },
        isObjectItem(item) {
            return typeof item === "object" && item !== null;
        },
        shareNative() {
            if (!this.post) {
                return;
            }

            if (navigator.share) {
                navigator.share({
                    title: this.post.title,
                    text: this.post.excerpt,
                    url: this.postUrl,
                });
                return;
            }

            this.openShareUrl("whatsapp");
        },
        openShareUrl(channel) {
            if (!this.post) {
                return;
            }

            const encodedUrl = encodeURIComponent(this.postUrl);
            const encodedTitle = encodeURIComponent(this.post.title);
            const encodedText = encodeURIComponent(`${this.post.title} - ${this.post.excerpt}`);
            const channels = {
                whatsapp: `https://api.whatsapp.com/send?text=${encodedText}%20${encodedUrl}`,
                linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
                x: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
            };

            const url = channels[channel];
            if (url) {
                window.open(url, "_blank", "noopener,noreferrer");
            }
        },
        copyPostUrl() {
            if (!this.post) {
                return;
            }

            navigator.clipboard
                .writeText(this.postUrl)
                .then(() => {
                    this.copyMessage = "Enlace copiado.";
                })
                .catch(() => {
                    this.copyMessage = "No se pudo copiar automaticamente. Copia la URL manualmente.";
                });
        },
    },
};
</script>

<style scoped>
#PostDetailView {
    --ink: #101828;
    --muted: #475467;
    --line: #dbe3ee;
    --accent: #146ef5;
    color: var(--ink);
    letter-spacing: 0;
    background: #ffffff;
    min-height: 100vh;
}

.post-hero {
    background: #f7f9fc;
    border-bottom: 1px solid var(--line);
    padding: 48px 0 64px;
    position: relative;
    overflow: hidden;
}

.post-hero::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image: linear-gradient(rgba(20, 110, 245, .05) 1px, transparent 1px), linear-gradient(90deg, rgba(20, 110, 245, .05) 1px, transparent 1px);
    background-size: 48px 48px;
    mask-image: linear-gradient(to right, #000, transparent 85%);
    pointer-events: none;
}

.post-breadcrumb {
    position: relative;
    z-index: 1;
    margin-bottom: 38px;
    font-size: 0.9rem;
    display: flex;
    gap: 8px;
    align-items: center;
}

.post-breadcrumb a {
    color: #075fcf;
    text-decoration: none;
}

.post-breadcrumb span {
    color: var(--muted);
}

.post-breadcrumb span:last-child {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.hero-content {
    position: relative;
    z-index: 1;
    max-width: 920px;
}

.post-kicker {
    margin: 0 0 8px;
    font-size: 0.86rem;
    letter-spacing: .12em;
    text-transform: uppercase;
    font-weight: 800;
    color: #075fcf;
}

.post-hero-title {
    font-size: clamp(2.2rem, 4vw, 3.8rem) !important;
    line-height: 1.1;
    font-weight: 800;
    margin: 0 !important;
    color: var(--ink) !important;
}

.hero-meta-row {
    margin-top: 28px;
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}

.post-date-badge {
    color: var(--muted);
    border-left: 1px solid #cbd5e1;
    padding: 2px 16px;
    font-size: 0.86rem;
    font-weight: 700;
}

.post-date-badge:first-child {
    border: 0;
    padding-left: 0;
}

.post-container {
    max-width: 1220px;
    padding: 0 15px 100px;
}

.article-layout {
    margin-top: 42px;
    position: relative;
    z-index: 2;
    display: grid;
    grid-template-columns: minmax(0, 780px) minmax(220px, 1fr);
    gap: clamp(30px, 5vw, 80px);
    align-items: start;
}

.featured-image-wrapper {
    margin: 0 0 44px;
    border-radius: 6px;
    overflow: hidden;
    background: #f4f7fb;
}

.featured-image {
    width: 100%;
    max-height: 520px;
    object-fit: cover;
    display: block;
}

.featured-caption {
    margin: 0;
    padding: 10px 14px;
    font-size: 0.88rem;
    color: var(--muted);
    background: #f7f9fc;
}

.content-wrapper {
    max-width: 780px;
}

.post-summary {
    border-left: 3px solid var(--accent);
    background: #f4f7fb;
    padding: 24px 28px;
    margin-bottom: 40px;
}

.summary-title {
    margin: 0 0 8px;
    font-size: 1.2rem;
    color: var(--ink);
    font-weight: 800;
}

.post-summary p {
    margin: 0;
    color: var(--muted);
    line-height: 1.8;
    white-space: pre-line;
}

.content-section {
    margin-bottom: 48px;
    scroll-margin-top: 32px;
}

.section-title {
    margin: 0 0 16px;
    color: var(--ink);
    font-size: clamp(1.3rem, 2.2vw, 1.9rem);
    font-weight: 800;
    line-height: 1.25;
}

.content-block + .content-block {
    margin-top: 20px;
}

.content-subheading {
    margin: 0;
    color: var(--ink);
    font-weight: 700;
    font-size: clamp(1.05rem, 1.8vw, 1.35rem);
    line-height: 1.35;
}

.content-paragraph {
    margin: 0;
    line-height: 1.8;
    color: var(--muted);
    white-space: pre-line;
}

.unordered-list,
.ordered-list {
    margin: 0;
    padding-left: 1.25rem;
    color: var(--muted);
}

.unordered-list li,
.ordered-list li {
    margin-bottom: 10px;
    line-height: 1.7;
}

.table-scroll {
    max-width: 100%;
    overflow-x: auto;
    border: 1px solid var(--line);
    border-radius: 6px;
    background: #ffffff;
}

.table-scroll:focus-visible {
    outline: 3px solid var(--accent);
    outline-offset: 3px;
}

.content-table {
    width: 100%;
    border-collapse: collapse;
    color: var(--muted);
}

.content-table caption {
    padding: 12px 14px;
    color: var(--ink);
    font-weight: 700;
    text-align: left;
}

.content-table th,
.content-table td {
    min-width: 120px;
    padding: 10px 14px;
    border-top: 1px solid var(--line);
    text-align: left;
    line-height: 1.6;
}

.content-table th {
    background: #f4f7fb;
    color: var(--ink);
    font-weight: 700;
}

.callout {
    border-left: 3px solid transparent;
    padding: 18px 22px;
}

.callout-title {
    margin: 0 0 4px;
    font-weight: 700;
}

.callout-text {
    margin: 0;
    line-height: 1.7;
}

.callout-info {
    background: #f0f6ff;
    border-color: #146ef5;
    color: #134466;
}

.callout-warning {
    background: #fff6e9;
    border-color: #da8c16;
    color: #68480f;
}

.callout-success {
    background: #edf9f3;
    border-color: #329266;
    color: #1f5637;
}

.inline-image-wrapper {
    margin: 8px auto;
    width: 100%;
    max-width: 760px;
    border-radius: 6px;
    overflow: hidden;
    border: 1px solid #d7e4ee;
    background: #f8fbfe;
}

.inline-image {
    width: auto;
    max-width: 100%;
    height: auto;
    max-height: 480px;
    margin: 0 auto;
    display: block;
}

.inline-caption {
    margin: 0;
    padding: 10px 12px;
    font-size: 0.86rem;
    color: #506375;
}

.takeaways {
    margin-bottom: 44px;
    padding: 26px 28px 16px;
    border-top: 1px solid var(--line);
    border-bottom: 1px solid var(--line);
}

.post-sidebar {
    position: sticky;
    top: 24px;
}

.toc-card {
    border-top: 2px solid var(--accent);
    padding: 22px 0;
}

.toc-title {
    margin: 0 0 10px;
    font-weight: 700;
    color: var(--ink);
}

.toc-list {
    margin: 0;
    padding-left: 1.2rem;
}

.toc-list li {
    margin-bottom: 8px;
}

.toc-list a {
    color: var(--muted);
    text-decoration: none;
    line-height: 1.5;
}

.toc-list a:hover {
    color: #075fcf;
    text-decoration: underline;
}

.share-section {
    margin-top: 48px;
    padding: 24px 0;
    border-top: 1px solid var(--line);
    border-bottom: 1px solid var(--line);
}

.share-title {
    margin: 0 0 12px;
    font-weight: 700;
    color: var(--ink);
}

.share-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}

.share-btn {
    border: 1px solid var(--line);
    background: #fff;
    color: #075fcf;
    padding: 10px 14px;
    border-radius: 6px;
    font-size: 0.88rem;
    font-weight: 600;
    text-decoration: none;
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.share-btn:hover {
    background: #edf4ff;
    text-decoration: none;
}

.share-btn:focus-visible,
.toc-list a:focus-visible {
    outline: 3px solid var(--accent);
    outline-offset: 2px;
}

.copy-message {
    margin: 8px 0 0;
    font-size: 0.86rem;
    color: var(--muted);
}

.author-section {
    margin-top: 28px;
    padding-top: 24px;
}

.author-card {
    border-left: 3px solid var(--accent);
    padding: 4px 20px;
}

.author-label {
    margin: 0 0 4px;
    font-size: 0.8rem;
    color: #5b7081;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.author-name {
    margin: 0;
    font-size: 1.06rem;
    color: var(--ink);
    font-weight: 700;
}

.not-found-wrapper {
    margin-top: 36px;
    padding: 40px 0;
}

@media (max-width: 1080px) {
    .article-layout {
        grid-template-columns: minmax(0, 1fr);
    }

    .post-sidebar {
        position: static;
        grid-row: 2;
    }
}

@media (max-width: 768px) {
    .post-hero {
        padding: 36px 0 48px;
    }

    .takeaways {
        padding: 20px 0 10px;
    }
}

@media (max-width: 576px) {
    .post-breadcrumb {
        font-size: 0.82rem;
        flex-wrap: wrap;
    }

    .post-date-badge {
        font-size: 0.8rem;
        padding: 2px 10px;
    }

    .post-summary {
        padding: 20px;
    }

    .article-layout {
        margin-top: 26px;
    }

    .post-breadcrumb {
        margin-bottom: 26px;
    }

    .featured-caption,
    .inline-caption {
        font-size: 0.8rem;
    }

    .share-btn {
        width: 100%;
        text-align: center;
    }
}
</style>
